import type { FC } from 'react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUpdateEffect } from 'ahooks';
import { Image, Toast, Skeleton } from 'antd-mobile';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import fastDeepEqual from 'fast-deep-equal';

import type {
  ProductInfoCustomization,
  ProductInfoComboItem,
  ProductInfoComboProductType,
  BogoPriceReductionDataType,
  MenuListProductPromotionItem,
} from '@/apis/product/types';
import useQuery from '@/hooks/useQuery';
import useAppTitle from '@/hooks/useTitle';
import { useChannelSupportSafeArea } from '@/hooks/useSupportSafeArea';
import { getCDNAssets, parseObj } from '@/utils';

import DeatilFooterAction from './FooterAction';
import DCFooterAction from './FooterAction/DCFooterAction';
import { ProductCardCountStepper } from '../Menu/List/Card/NumButton';
import type { ProductDetailOptionsProps } from './Options/index';
import ProductDetailOptions from './Options/index';
import useProductDetail from './useDetail';
import useProductDetailEditorCache, {
  fmtDCProductComboItemsToBody,
  getExtraCustomizationCacheKey,
  getProductComboAndCustomizationDiffPrice,
} from './useEditor';
import ProductCustomSelectorPopup from './CustomSelector/Popup';
import type { DCCustomListPopupProps } from './DCCustomProduct/Popup';
import DCCustomListPopup, { checkDCProductInfoComboItemEmpty } from './DCCustomProduct/Popup';
import { useStoreDateInfoWithLocation } from '../external';
import { trackingProductDetailPV, trackAddToCartByDetailEV } from '../external/trackinfo';
import { findCurProduct, checkDCComboCount, checkDCComboIsEmpty, checkSingleCombo } from './func';

import './index.less';

const getImgBgPath = (daypartCode?: number | string) => {
  if (Number(daypartCode) === 1) {
    return getCDNAssets('detail_top_back_breakfast.png');
  }
  return getCDNAssets('detail_top_back_dinner.png');
};

type UrlPathParams = {
  productCode: string;
};

type UrlPathQuery = {
  daypartCode?: string;
  orderType?: string;
  storeCode?: string;
  beCode?: string;
  beType?: string;
  /** 是够来自于购物车 true: '1' */
  fromCart?: string;
  /** for `fromCart` 购物车中的 sequence */
  sequence?: string;
  couponName?: string;
  couponCode?: string;
  couponId?: string;
  promotionId?: string;
  disabledCount?: string;
  /** JSON string */
  productPromotions?: string;
};

const ProductDetailPage: FC = () => {
  const params = useParams<UrlPathParams>();
  const query = useQuery<UrlPathQuery>();
  const navigate = useNavigate();

  useAppTitle({
    navBackgroundColor: '00FFFFFF',
    appletStyle: 'black',
    backBtnVisible: '1',
    appletTitleBarVisible: '1',
  });

  const fromCart = query?.fromCart === '1';

  const { curDateInfo } = useStoreDateInfoWithLocation(query?.orderType ? Number(query?.orderType) : 1);

  const [count, setCount] = useState(fromCart ? 0 : 1);
  const [matchedSpecsCode, setMatchedSpecsCode] = useState<string>();
  const [popupDataCache, setPopupDataCache] = useState<{
    productCode?: string | undefined;
    choicesType?: number;
    combo?: ProductInfoComboItem;
    comboProduct?: ProductInfoComboProductType;
    /** extraCustomizationCache key */
    extraCacheKey?: string;
  }>();
  const [dcCustomVisible, setDCCustomVisible] = useState(false);

  const queryProductPromotions = useMemo(
    () =>
      query?.productPromotions
        ? parseObj<MenuListProductPromotionItem[]>(decodeURIComponent(query.productPromotions))
        : undefined,
    [query?.productPromotions],
  );

  const {
    productData: originProductRes,
    productDesc,
    productLoading,
    couponPrice,
  } = useProductDetail(
    {
      productCode: params.productCode,
      daypartCode: Number(query?.daypartCode),
      orderType: Number(query?.orderType),
      storeCode: query?.storeCode,
      pageSource: fromCart ? 2 : 1,
      beCode: query?.beCode,
      date: curDateInfo?.date,
      time: curDateInfo?.time,
      couponCode: query?.couponCode,
      couponId: query?.couponId,
      promotionId: query?.promotionId,
      productPromotions: queryProductPromotions,
    },
    !fromCart,
    {
      onCompleted: (res: { success: boolean; message: string }) => {
        if (res?.success === false && res.message) {
          Toast.show({
            content: res.message,
            position: 'center',
          });
        }
      },
    },
  );

  const {
    productCache,
    restProductCache,
    extraCustomizationCache,
    updateExtraCustomizationCache,
    initCountFromCart,
    curCartProduct,
    initProductForCart,
    disabledCountStepper,
    changeCustomizationOptionCheck,
    changeCustomizationItemCheck,
    changeComboItemsProductDefault,
    changeComboItemsProductMutliCount,
    addToCart,
    saveToCart,
    createPOSTBogoPriceReductionReq,
  } = useProductDetailEditorCache(
    originProductRes?.product,
    fromCart
      ? {
          cartType: 1,
          daypartCode: query?.daypartCode,
          orderType: Number(query?.orderType),
          storeCode: query?.storeCode,
          sequence: Number(query.sequence),
        }
      : undefined,
    {
      couponCode: query?.couponCode,
      couponId: query?.couponId,
      promotionId: query?.promotionId,
    },
  );
  useEffect(() => {
    if (fromCart && initCountFromCart && initCountFromCart > 0) {
      setCount(initCountFromCart);
    }
  }, [fromCart, initCountFromCart]);

  const [defalutMatchedSpecsCode, activedSpecsGroupCodes] = useMemo(() => {
    const _specTypes = productCache?.specTypes;
    if (_specTypes && !isEmpty(_specTypes)) {
      const _codes = _specTypes
        .flatMap((t) => t.code && t.specs && t.specs?.map((s) => t.code && s.code && `${t.code}-${s.code}`))
        .filter(Boolean) as string[];
      const _activeCodes = (productCache?.products?.map((p) => p.matchedSpecsCode).filter(Boolean) as string[]) || [];

      const _defaultCode = _activeCodes.find((c) => c.split(',').every((cc) => _codes.includes(cc)));

      return [_defaultCode, _activeCodes];
    }

    return [undefined, undefined];
  }, [productCache?.products, productCache?.specTypes]);

  useEffect(() => {
    if (!matchedSpecsCode && defalutMatchedSpecsCode) {
      setMatchedSpecsCode(defalutMatchedSpecsCode);
    }
  }, [defalutMatchedSpecsCode, matchedSpecsCode]);

  const curProduct = useMemo(() => findCurProduct(productCache, matchedSpecsCode), [productCache, matchedSpecsCode]);
  // 是否是付费会员商品
  const isMemberProduct = useMemo(() => curProduct?.promotionInfo?.pmtType === 3, [curProduct?.promotionInfo?.pmtType]);
  const isDCProductType = curProduct?.type === 7;

  const maxPurchaseQuantity = useMemo(
    () => originProductRes?.maxPurchaseQuantity ?? 99,
    [originProductRes?.maxPurchaseQuantity],
  );

  const cartInitDiffPrice = useMemo(() => {
    if (fromCart && curCartProduct?.coupon) {
      return getProductComboAndCustomizationDiffPrice(initProductForCart?.value, initProductForCart?.extra);
    }
    return undefined;
  }, [curCartProduct?.coupon, fromCart, initProductForCart?.extra, initProductForCart?.value]);

  const totalPrice = useMemo(() => {
    if (curProduct?.price && curProduct.type !== 7) {
      const _extraDiffPrice = getProductComboAndCustomizationDiffPrice(curProduct, extraCustomizationCache);
      const getUnitPrice = (isSmart?: boolean) => {
        const _price = (isSmart ? curProduct.smartPrice ?? curProduct.price : curProduct.price) as number;
        let _originUnitPrice = _price;

        _originUnitPrice += _extraDiffPrice;

        if (
          fromCart &&
          cartInitDiffPrice != null &&
          curCartProduct?.realPrice != null &&
          // TODO
          curProduct.promotionInfo?.pmtType !== 2
        ) {
          _originUnitPrice = _originUnitPrice - _price + curCartProduct.realPrice - cartInitDiffPrice;
        }

        const _couponOriginPrice =
          Number(query?.orderType) === 2 ? couponPrice?.deliveryPrice : couponPrice?.eatInPrice;
        if (!fromCart && _couponOriginPrice && _couponOriginPrice === _price && couponPrice?.discountPrice != null) {
          const _diffPrice = _couponOriginPrice - couponPrice.discountPrice || 0;
          _originUnitPrice = _originUnitPrice - _diffPrice;
        }

        return _originUnitPrice;
      };

      const _promotionLimitNum = curProduct.promotionLimitNum || 0;

      if (_promotionLimitNum >= 1) {
        if (_promotionLimitNum < count) {
          const _other = count - _promotionLimitNum;
          return getUnitPrice(true) * _promotionLimitNum + getUnitPrice() * _other;
        } else {
          return getUnitPrice(true) * count;
        }
      }

      return getUnitPrice() * count;
    }
    return undefined;
  }, [
    cartInitDiffPrice,
    count,
    couponPrice,
    curCartProduct?.realPrice,
    curProduct,
    extraCustomizationCache,
    fromCart,
    query?.orderType,
  ]);

  // DC price
  const initDCTotalPrice = useMemo(() => {
    const _price = fromCart ? curCartProduct?.realPrice : curProduct?.smartPrice ?? curProduct?.price;
    if (_price != null) {
      return _price * count;
    }
    return undefined;
  }, [count, curCartProduct?.realPrice, curProduct?.price, curProduct?.smartPrice, fromCart]);
  const initDCCutPrice = useMemo(
    () =>
      !fromCart && curProduct?.alcPrice != null && curProduct?.price != null
        ? curProduct.alcPrice - curProduct.price
        : undefined,
    [curProduct?.alcPrice, curProduct?.price, fromCart],
  );
  const [dcPriceObj, setDCPriceObj] = useState<{ changed?: boolean; price?: BogoPriceReductionDataType }>({
    changed: false,
  });
  useEffect(() => {
    const _originData = fromCart ? initProductForCart?.value : originProductRes?.product;
    if (_originData && curProduct && curProduct.type === 7) {
      if (matchedSpecsCode && _originData.type === 3) {
        if (!fastDeepEqual(findCurProduct(_originData, matchedSpecsCode), curProduct)) {
          setDCPriceObj((d) => ({ ...d, changed: true, price: undefined }));
        } else {
          setDCPriceObj((d) => (d.changed === true ? { ...d, price: undefined } : d));
        }
      } else if (_originData.type !== 3) {
        if (!fastDeepEqual(_originData, curProduct)) {
          setDCPriceObj((d) => ({ ...d, changed: true, price: undefined }));
        } else {
          setDCPriceObj((d) => (d.changed === true ? { ...d, price: undefined } : d));
        }
      }
    }
  }, [curProduct, fromCart, initProductForCart?.value, matchedSpecsCode, originProductRes?.product]);
  useEffect(() => {
    if (!isEmpty(extraCustomizationCache) && isDCProductType) {
      if (!fromCart || (fromCart && !fastDeepEqual(initProductForCart?.extra, extraCustomizationCache))) {
        setDCPriceObj((d) => ({ ...d, changed: true, price: undefined }));
      }
    }
  }, [extraCustomizationCache, fromCart, initProductForCart?.extra, isDCProductType]);
  useUpdateEffect(() => {
    if (!fromCart) {
      setDCPriceObj((d) => ({ ...d, changed: true, price: undefined }));
    }
  }, [count]);
  useUpdateEffect(() => {
    if (fromCart && initCountFromCart && count > 0 && initCountFromCart !== count) {
      setDCPriceObj((d) => ({ ...d, changed: true, price: undefined }));
    }
  }, [count, initCountFromCart]);

  const onCountStepperAdd = useCallback(() => {
    const _newCount = count + 1;
    if (curProduct?.promotionLimitNum && curProduct.promotionLimitNum + 1 === _newCount) {
      if (
        (isMemberProduct && !curProduct.promotionInfo?.withOrder && curProduct.promotionInfo?.hasRights) ||
        !isMemberProduct
      ) {
        Toast.show({
          content: '达到优惠数量上限，超出使用原价购买',
          position: 'center',
          duration: 2000,
        });
      }
    }

    setCount((i) => i + 1);
  }, [
    count,
    curProduct?.promotionInfo?.hasRights,
    curProduct?.promotionInfo?.withOrder,
    curProduct?.promotionLimitNum,
    isMemberProduct,
  ]);

  const onCustomizationOptionSelect = useCallback<Required<ProductDetailOptionsProps>['onCustomizationOptionSelect']>(
    (code, option) => {
      changeCustomizationOptionCheck(1, code, option);
    },
    [changeCustomizationOptionCheck],
  );
  const onCustomizationOptionDeSelect = useCallback<
    Required<ProductDetailOptionsProps>['onCustomizationOptionDeSelect']
  >(
    (code, option) => {
      changeCustomizationOptionCheck(0, code, option);
    },
    [changeCustomizationOptionCheck],
  );
  const onCustomizationItemSelect = useCallback<Required<ProductDetailOptionsProps>['onCustomizationItemSelect']>(
    (code, item, itemValue) => {
      changeCustomizationItemCheck(code, item, itemValue);
    },
    [changeCustomizationItemCheck],
  );
  const onComboItemsProductSelect = useCallback<Required<ProductDetailOptionsProps>['onComboItemsProductSelect']>(
    (code, combo, p) => {
      changeComboItemsProductDefault(code, combo, p);
    },
    [changeComboItemsProductDefault],
  );
  const onComboItemsProductCustom = useCallback<Required<ProductDetailOptionsProps>['onComboItemsProductCustom']>(
    (code, type, combo, p, pIndex) => {
      setPopupDataCache({
        productCode: code,
        choicesType: type,
        combo,
        comboProduct: p,
        extraCacheKey: p.code && getExtraCustomizationCacheKey(combo, p.code, pIndex),
      });
    },
    [],
  );
  const onComboItemsProductMultiAdd = useCallback<Required<ProductDetailOptionsProps>['onComboItemsProductMultiAdd']>(
    (code, combo, p) => {
      changeComboItemsProductMutliCount(code, combo, p, 1);
    },
    [changeComboItemsProductMutliCount],
  );
  const onComboItemsProductMultiSub = useCallback<Required<ProductDetailOptionsProps>['onComboItemsProductMultiAdd']>(
    (code, combo, p) => {
      changeComboItemsProductMutliCount(code, combo, p, -1);
    },
    [changeComboItemsProductMutliCount],
  );
  const onComboItemsProductMultiSelectedClick = useCallback<
    Required<ProductDetailOptionsProps>['onComboItemsProductMultiSelectedClick']
  >((code, combo, p) => {
    if (p.hasCustomization === 1) {
      if (p.quantity && p.quantity > 1) {
        setDCCustomVisible(true);
      } else if (p.quantity === 1) {
        setPopupDataCache({
          productCode: p?.code,
          choicesType: undefined,
          combo,
          comboProduct: p,
          extraCacheKey: combo && p?.code && getExtraCustomizationCacheKey(combo, p.code, 0),
        });
      }
    }
  }, []);
  const onSpecsCodeSelect = useCallback<Required<ProductDetailOptionsProps>['onSpecsCodeSelect']>((code, item) => {
    setMatchedSpecsCode((_code) => {
      if (_code) {
        const _new = _code
          .split(',')
          .map((c) => (item.code && c.indexOf(item.code) === 0 ? code : c))
          .join(',');
        return _new;
      }
      return code;
    });
  }, []);

  const onDCFooterCustomClick = useCallback(() => {
    if (!checkDCProductInfoComboItemEmpty(curProduct?.comboItems)) {
      setDCCustomVisible(true);
    } else {
      Toast.show({
        content: '请选择餐品',
        position: 'center',
      });
    }
  }, [curProduct?.comboItems]);
  const onDCFooterCustomItemEdit = useCallback<Required<DCCustomListPopupProps>['onItemEdit']>((c, p, pIdx) => {
    setPopupDataCache({
      productCode: p?.code,
      choicesType: undefined,
      combo: c,
      comboProduct: p,
      extraCacheKey: c && p?.code && getExtraCustomizationCacheKey(c, p.code, pIdx),
    });
  }, []);
  const onDCFooterCustomItemDel = useCallback<Required<DCCustomListPopupProps>['onItemDel']>(
    (c, p, pIdx, total) => {
      const _key = c && p?.code && getExtraCustomizationCacheKey(c, p.code, pIdx);

      if (_key) {
        updateExtraCustomizationCache((d) => {
          if (pIdx != null && c.quantity && c.quantity > pIdx + 1) {
            let _k = _key;
            for (let i = pIdx + 1; i < c.quantity; i++) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const _curKey = getExtraCustomizationCacheKey(c, p.code!, i);
              d[_k] = d[_curKey];
              _k = _curKey;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const _lastKey = getExtraCustomizationCacheKey(c, p.code!, c.quantity - 1);
            delete d[_lastKey];
          } else {
            delete d[_key];
          }
        });
        changeComboItemsProductMutliCount(curProduct?.code, c, p, -1);

        // 删除最后一条, 关闭弹窗
        if (total === 1) {
          setDCCustomVisible(false);
        }
      }
    },
    [changeComboItemsProductMutliCount, curProduct?.code, updateExtraCustomizationCache],
  );

  const onCustomPopupComfirm = useCallback(
    (customization?: ProductInfoCustomization) => {
      // TODO popupDataCache data
      const _key = popupDataCache?.extraCacheKey;

      if (customization && _key) {
        updateExtraCustomizationCache((d) => {
          d[_key] = customization;
        });
      }
      return true;
    },
    [popupDataCache?.extraCacheKey, updateExtraCustomizationCache],
  );

  const onReset = useCallback(() => {
    setMatchedSpecsCode(defalutMatchedSpecsCode);
    restProductCache();
  }, [defalutMatchedSpecsCode, restProductCache]);

  const onAddCart = useCallback(async () => {
    try {
      if (fromCart) {
        await saveToCart(count, matchedSpecsCode, {
          daypartCode: query?.daypartCode || '',
          orderType: Number(query?.orderType),
          storeCode: query?.storeCode || '',
          cartType: '1',
          beCode: query?.beCode,
          date: curDateInfo?.date,
          time: curDateInfo?.time,
        });

        navigate(-1);
      } else {
        const _originProduct = findCurProduct(originProductRes?.product, matchedSpecsCode);
        trackAddToCartByDetailEV(
          curProduct,
          count,
          query?.daypartCode,
          query?.orderType,
          isEqual(_originProduct, curProduct),
          totalPrice,
          query?.couponName,
        );

        await addToCart(count, matchedSpecsCode, {
          daypartCode: query?.daypartCode || '',
          orderType: Number(query?.orderType),
          storeCode: query?.storeCode || '',
          cartType: '1',
          beCode: query?.beCode,
          date: curDateInfo?.date,
          time: curDateInfo?.time,
        });

        navigate(-1);
        // navigate(
        //   {
        //     pathname: '/product',
        //     search: new URLSearchParams({
        //       storeCode: query?.storeCode || '',
        //       orderType: query?.orderType || '',
        //     }).toString(),
        //   },
        //   { replace: true },
        // );
      }
    } catch (err) {
      const catchErrMsg = (err as Error)?.message;
      const errMsg = typeof catchErrMsg === 'string' && catchErrMsg ? catchErrMsg : '加入购物车失败';

      console.error(err);

      Toast.show({
        content: errMsg,
        position: 'center',
      });
    }
  }, [
    addToCart,
    count,
    curDateInfo?.date,
    curDateInfo?.time,
    curProduct,
    fromCart,
    matchedSpecsCode,
    navigate,
    originProductRes?.product,
    query?.beCode,
    query?.couponName,
    query?.daypartCode,
    query?.orderType,
    query?.storeCode,
    saveToCart,
    totalPrice,
  ]);

  const onNormalAddCart = useCallback(async () => {
    const checkedStatus = curProduct?.type === 2 && checkSingleCombo(curProduct?.comboItems);

    if (checkedStatus) {
      Toast.show({
        content: `您还需确认${checkedStatus.name}`,
        position: 'center',
      });

      return;
    }
    await onAddCart();
  }, [curProduct?.type, curProduct?.comboItems, onAddCart]);

  const onDCFooterAddCart = useCallback(async () => {
    if (checkDCComboIsEmpty(curProduct?.comboItems)) {
      Toast.show({
        content: '请选择餐品',
        position: 'center',
      });

      return;
    }
    const diffCountObj = checkDCComboCount(curProduct?.comboItems);
    if (diffCountObj) {
      Toast.show({
        content: `您还需选择${diffCountObj.diff}份${diffCountObj.name}`,
        position: 'center',
      });

      return;
    }

    if (dcPriceObj.changed && !dcPriceObj.price && originProductRes?.calcPrice) {
      const _comboItems = fmtDCProductComboItemsToBody(curProduct?.comboItems, extraCustomizationCache);
      if (!curProduct) {
        return;
      }
      try {
        const [res] = await createPOSTBogoPriceReductionReq({
          daypartCode: Number(query?.daypartCode),
          needCalType: 1,
          orderType: Number(query?.orderType),
          storeCode: query?.storeCode || '',
          beCode: query?.beCode,
          date: curDateInfo?.date,
          time: curDateInfo?.time,
          products: [
            {
              code: curProduct.code,
              quantity: count,
              type: curProduct.type,
              comboItems: _comboItems,
              promotionId: query?.promotionId,
              promotionInfo: curProduct.promotionInfo,
            },
          ],
        }).ready();

        setDCPriceObj({ changed: true, price: res });
      } catch (error) {
        const catchErrMsg = (error as Error)?.message;
        const errMsg = typeof catchErrMsg === 'string' && catchErrMsg ? catchErrMsg : '计算价格失败';

        Toast.show({
          content: errMsg,
          position: 'center',
        });
      }
    } else {
      await onAddCart();
    }
  }, [
    count,
    createPOSTBogoPriceReductionReq,
    curDateInfo?.date,
    curDateInfo?.time,
    curProduct,
    dcPriceObj.changed,
    dcPriceObj.price,
    extraCustomizationCache,
    onAddCart,
    originProductRes?.calcPrice,
    query?.beCode,
    query?.daypartCode,
    query?.orderType,
    query?.promotionId,
    query?.storeCode,
  ]);

  useEffect(() => {
    if (!fromCart) {
      const _product = findCurProduct(originProductRes?.product, matchedSpecsCode);
      trackingProductDetailPV(_product, query?.daypartCode, query?.orderType);
    }
  }, [fromCart, matchedSpecsCode, originProductRes, query?.daypartCode, query?.orderType]);

  const showActions = useMemo(() => productLoading === false && !isEmpty(productCache), [productCache, productLoading]);
  const showResetAction = useMemo(() => {
    const _p = productCache?.products
      ? productCache.products.find((p) => p.matchedSpecsCode === matchedSpecsCode)
      : productCache;

    if (_p?.customizationMode === 1 && _p.customization) {
      const { options, items } = _p.customization;
      if (!isEmpty(options) || !isEmpty(items)) {
        return true;
      }
    } else if (!isEmpty(_p?.comboItems)) {
      return true;
    }
    return false;
  }, [matchedSpecsCode, productCache]);

  const supportSafeArea = useChannelSupportSafeArea();

  return (
    <div className="product-detail-container">
      <div className="product-detail-top-bg" style={{ backgroundImage: `url(${getImgBgPath(query?.daypartCode)})` }}>
        <Image className="product-detail-top-bg-img" placeholder={null} lazy fit="scale-down" src={curProduct?.image} />
      </div>
      <div className="product-detail-block">
        <div className="product-detail-block-header">
          {productLoading ? (
            <Skeleton animated style={{ '--height': '28px', '--width': '120px', margin: 0 }} />
          ) : (
            <div className="product-detail-block-header-title">{curProduct?.name}</div>
          )}
          {showActions && (
            <ProductCardCountStepper
              subDisabled={disabledCountStepper || query?.disabledCount === '1'}
              addDisabled={disabledCountStepper || query?.disabledCount === '1'}
              count={count}
              minCount={1}
              maxCount={maxPurchaseQuantity}
              isGaryStyle
              onAdd={onCountStepperAdd}
              onSub={() => setCount((i) => i - 1)}
            />
          )}
        </div>
        <div className="product-detail-block-content">
          {productLoading && <Skeleton.Paragraph animated />}
          <p className="product-detail-block-content-desc">{curProduct?.desc}</p>

          <ProductDetailOptions
            data={productCache}
            matchedSpecsCode={matchedSpecsCode}
            activedSpecsGroupCodes={activedSpecsGroupCodes}
            extraCustomizationData={extraCustomizationCache}
            onSpecsCodeSelect={onSpecsCodeSelect}
            onCustomizationOptionSelect={onCustomizationOptionSelect}
            onCustomizationOptionDeSelect={onCustomizationOptionDeSelect}
            onCustomizationItemSelect={onCustomizationItemSelect}
            onComboItemsProductSelect={onComboItemsProductSelect}
            onComboItemsProductCustom={onComboItemsProductCustom}
            onComboItemsProductMultiAdd={onComboItemsProductMultiAdd}
            onComboItemsProductMultiSub={onComboItemsProductMultiSub}
            onComboItemsProductMultiSelectedClick={onComboItemsProductMultiSelectedClick}
          />

          {productDesc && (
            <div className="product-detail-block-content-explain">
              <b>说明</b>
              <p>{productDesc}</p>
            </div>
          )}
        </div>
        {showActions && (
          <div className="product-detail-block-footer" data-supportsafearea={supportSafeArea}>
            {isDCProductType ? (
              <DCFooterAction
                calcPrice={originProductRes?.calcPrice}
                fromCart={fromCart}
                cutPrice={dcPriceObj?.changed ? dcPriceObj.price?.cut : initDCCutPrice}
                price={dcPriceObj?.changed ? dcPriceObj.price?.price : initDCTotalPrice}
                onCustomClick={onDCFooterCustomClick}
                onAddCart={onDCFooterAddCart}
              />
            ) : (
              <DeatilFooterAction
                fromCart={fromCart}
                price={totalPrice}
                onReset={showResetAction ? onReset : undefined}
                onAddCart={onNormalAddCart}
              />
            )}
          </div>
        )}
      </div>

      {isDCProductType && (
        <DCCustomListPopup
          itemsExtraCustomizationObj={extraCustomizationCache}
          data={curProduct}
          count={count}
          visible={dcCustomVisible}
          onMaskClick={() => setDCCustomVisible(false)}
          onOk={() => setDCCustomVisible(false)}
          onItemEdit={onDCFooterCustomItemEdit}
          onItemDel={onDCFooterCustomItemDel}
        />
      )}

      <ProductCustomSelectorPopup
        visible={Boolean(popupDataCache)}
        onClose={() => setPopupDataCache(undefined)}
        initCustomization={
          popupDataCache?.extraCacheKey ? extraCustomizationCache[popupDataCache.extraCacheKey] : undefined
        }
        data={popupDataCache?.comboProduct}
        params={{
          daypartCode: Number(query?.daypartCode),
          orderType: Number(query?.orderType),
          storeCode: query?.storeCode,
          beCode: query?.beCode,
          beType: query?.beType,
          date: curDateInfo?.date,
          time: curDateInfo?.time,
        }}
        onComfirm={onCustomPopupComfirm}
      />
    </div>
  );
};

export default ProductDetailPage;
